import { DatePicker, IconFont, Text } from '@byecode/ui'
import {
    type DateConfig,
    type DateField as DateFieldConfig,
    DateFieldUnit,
    type DateValue,
    type FieldInputADTValue,
    type FieldProtocol
} from '@lighthouse/core'
import { add, endOfDay, isValid, lightFormat, parse, startOfDay } from 'date-fns'
import { clamp, max } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { dateUnitToMode } from '../../../../constants'
import { extractLunarDate, format2LunarDate, getDateUnitByFormat } from '../../../../utils'
import { useAppThemeContext } from '../../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../../FillPicker'
import { defaultFormat, minPopoverWidth } from '../../constant'
import type { FieldBaseProps } from '../../types'
import * as CM from '../commonStyles'
import { getRelativeDate, getTimestamp } from './help'

interface DateFieldProps extends FieldBaseProps {
    dateConfig: DateConfig
    value?: DateValue
    dateField?: DateFieldConfig | FieldProtocol
}

const SCxContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-right: 8px;
    font-size: var(--font-size-normal);

    &[data-field-disabled='true'] {
        cursor: not-allowed;
    }
`

const SCxClear = styled(IconFont)`
    margin-right: 8px;
    cursor: pointer;
`

const SCxInput = styled.input<{ placeholderColor?: string }>`
    width: 100%;
    padding: 0 8px;
    height: 20px;
    line-height: 20px;
    color: inherit;

    ::placeholder {
        font-weight: var(--font-weight-normal);
        color: ${({ placeholderColor }) => placeholderColor || 'var(--color-gray-300)'};
    }
`

const SCxPreview = styled.div`
    width: 100%;
    padding: 8px 0;
    min-height: 38px;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    * {
        line-height: 20px;
    }
`

const DateField: React.FC<DateFieldProps> = props => {
    const { readOnly, dateConfig, onChange, value = '', dateField, isControlled, language } = props
    const { theme } = useAppThemeContext()
    const fieldType = dateField?.type || 'date'

    const { placeholder, date, breakPoint } = dateConfig ?? {}
    const { placeholderColor, fontFamily } = useMemo(
        () => ({
            placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
            fontFamily: breakPoint?.font?.fontFamily
        }),
        [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
    )
    const { dateFormat, range = 'ALL', customDays } = date ?? {}
    const withLunarFormat = useMemo(() => {
        if (dateField?.type === 'date') {
            return (dateField as DateFieldConfig).date.format ?? defaultFormat
        }

        if (dateField?.type === 'text') {
            return dateFormat ?? 'yyyy/MM/dd'
        }

        return defaultFormat
    }, [dateField, dateFormat])

    const [isLunar, finalFormat] = useMemo(() => {
        const includesLunar = withLunarFormat.includes('lunar-')
        const normalFormat = withLunarFormat
            .split(' ')
            .map(item => item.replace('lunar-', ''))
            .join(' ')
        return [includesLunar, normalFormat]
    }, [withLunarFormat])

    const { unit } = useMemo(() => getDateUnitByFormat(finalFormat), [finalFormat])

    const panelMode = useMemo(() => {
        return dateUnitToMode[unit]
    }, [unit])

    const timestamp = useMemo(() => {
        if (fieldType === 'text') {
            if (!value) {
                return value
            }

            // 农历日期格式
            if (isLunar) {
                return extractLunarDate(value.toString())?.getTime() || ''
            }

            const date = parse(value.toString(), finalFormat, new Date())
            if (isValid(date)) {
                return date.getTime()
            }

            return ''
        }

        return getTimestamp(value)
    }, [fieldType, finalFormat, isLunar, value])

    const containerRef = useRef<HTMLDivElement>(null)

    const inputRef = useRef<HTMLInputElement>(null)

    const [data, setData] = useState<string | number>(timestamp)

    const [isHover, setIsHover] = useState(false)

    const [opened, setOpened] = useState(false)

    const [isFocus, setIsFocus] = useState(false)

    const inputWidth = containerRef.current?.getBoundingClientRect()?.width ?? 0

    const popoverWidth = useMemo(() => clamp(minPopoverWidth, 380, inputWidth), [inputWidth])

    const usedValue = useMemo(() => {
        if (isControlled) {
            if (!value) {
                return value
            }
            if (fieldType === 'text') {
                // 农历日期格式
                if (isLunar) {
                    return extractLunarDate(value.toString())?.getTime() || ''
                }

                const date = parse(value.toString(), finalFormat, new Date())
                if (isValid(date)) {
                    return date.getTime()
                }

                return ''
            }
            return value
        }
        return data
    }, [data, fieldType, finalFormat, isControlled, isLunar, value])

    const [minDate, maxDate] = useMemo(() => {
        switch (range) {
            case 'AFTER_TODAY': {
                return [startOfDay(new Date()), undefined]
            }
            case 'BEFORE_TODAY': {
                return [undefined, endOfDay(new Date())]
            }
            case 'AFTER_CUSTOM': {
                return [
                    customDays
                        ? add(startOfDay(new Date()), {
                              days: max(customDays, 0)
                          })
                        : undefined,
                    undefined
                ]
            }
            default: {
                return []
            }
        }
    }, [customDays, range])

    const handleChange = useCallback(
        (date?: Date) => {
            const newDate = date?.valueOf()
            setData(newDate ?? '')
            setIsHover(false)
            inputRef.current?.blur()

            if (fieldType === 'text') {
                if (!date) {
                    onChange?.({ type: 'text', value: '' })
                    return
                }

                if (isLunar) {
                    onChange?.({
                        type: 'text',
                        value: format2LunarDate(date, withLunarFormat)
                    })

                    return
                }
                onChange?.({ type: 'text', value: lightFormat(date, finalFormat) })
            } else {
                onChange?.({ type: 'date', value: newDate ?? '' })
            }
        },
        [fieldType, finalFormat, isLunar, onChange, withLunarFormat]
    )

    const valueDate = useMemo(() => {
        const currentValue = usedValue ? new Date(usedValue) : undefined
        if (!currentValue) {
            return
        }
        return isValid(currentValue) ? currentValue : undefined
    }, [usedValue])

    const handleClear = useCallback(() => {
        setData('')

        onChange?.({ type: fieldType, value: '' } as FieldInputADTValue)
    }, [fieldType, onChange])

    useEffect(() => {
        if (opened && inputRef.current) {
            inputRef.current.focus()
        }
    }, [inputRef, opened])

    return (
        <SCxContainer
            onMouseEnter={() => {
                !readOnly && setIsHover(true)
            }}
            onMouseLeave={() => {
                setIsHover(false)
            }}
            ref={containerRef}
            data-field-input
            data-field-active={opened}
            data-field-disabled={!!readOnly}
        >
            <DatePicker
                popoverProps={{
                    width: popoverWidth,
                    opened,
                    onChange: setOpened,
                    offsetOptions: {
                        mainAxis: 10,
                        crossAxis: -2
                    },
                    enableClickToggle: true,
                    returnFocus: false
                }}
                minDate={minDate}
                styles={{
                    dropdown: {
                        fontFamily
                    }
                }}
                maxDate={maxDate}
                inputRender={({ date, value, onChange }) => {
                    const currentTimestamp = Date.now()
                    const text = date ? (isLunar ? format2LunarDate(date, withLunarFormat) : value) : value

                    const suffixText =
                        date && !isLunar && unit === DateFieldUnit.DAY ? getRelativeDate(date, currentTimestamp, language) : ''

                    return (
                        <SCxPreview onClick={() => !readOnly && setIsFocus(true)} onBlur={() => setIsFocus(false)}>
                            {isFocus ? (
                                <SCxInput
                                    ref={inputRef}
                                    readOnly
                                    disabled={readOnly}
                                    value={text}
                                    onChange={onChange}
                                    placeholder={placeholder}
                                    placeholderColor={placeholderColor}
                                />
                            ) : (
                                <Text style={{ padding: '0 8px' }} color={date ? 'inherit' : placeholderColor || 'var(--color-gray-300)'}>
                                    {text || placeholder}
                                    {`${suffixText}`}
                                </Text>
                            )}
                        </SCxPreview>
                    )
                }}
                disabled={readOnly}
                format={finalFormat}
                mode={panelMode}
                showTime={unit === DateFieldUnit.MINUTE || unit === DateFieldUnit.DAY_MINUTE}
                disableSecond
                placeholder={placeholder}
                value={valueDate}
                onChange={handleChange}
            />
            <CM.IconWrapper data-field-icon>
                {usedValue && (opened || isHover) && <SCxClear type="CloseCircle" size={18} onClick={handleClear} />}
                {!readOnly && <IconFont style={{ marginRight: 4 }} type="ArrowDownSmall" size={16} />}
            </CM.IconWrapper>
        </SCxContainer>
    )
}

export default DateField
